import type { Locale } from './types';

/**
 * Date and number formatting, done explicitly rather than through `Intl`.
 *
 * WHY NOT `Intl`. Chrome reports `kk` as a supported locale and then formats it
 * from the CLDR root: `Intl.DateTimeFormat('kk-KZ', { dateStyle: 'long' })`
 * returns `2026 M07 24` — a placeholder where the month name should be — and
 * `Intl.NumberFormat('kk-KZ')` groups with a comma instead of a space. Node with
 * full ICU gets both right, which is exactly why this is easy to miss: it looks
 * correct everywhere except in the browser the product runs in.
 *
 * Russian and English are fine through `Intl`. Using it for those two and hand
 * formatting the third would mean Kazakh users saw a different date layout from
 * everyone else, and any future locale would be a coin toss. So all three are
 * formatted from the same explicit patterns, and month names come from the
 * dictionaries — where a translator can see and fix them.
 *
 * The patterns themselves suit the product: an operations tool wants
 * `24.07.2026 13:10`, unambiguous and the same width every time, not `7/24/26`.
 */

interface NumberStyle {
  /** Between thousands. A no-break space, so a number never wraps mid-value. */
  group: string;
  decimal: string;
}

const NUMBER_STYLE: Record<Locale, NumberStyle> = {
  kk: { group: ' ', decimal: ',' },
  ru: { group: ' ', decimal: ',' },
  en: { group: ',', decimal: '.' },
};

const pad = (value: number): string => String(value).padStart(2, '0');

/** Accepts what an API actually returns: a Date, an ISO string, or a timestamp. */
const toDate = (value: Date | string | number): Date | null => {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatNumber = (locale: Locale, value: number, decimals = 0): string => {
  const style = NUMBER_STYLE[locale];

  if (!Number.isFinite(value)) return '';

  const fixed = Math.abs(value).toFixed(decimals);
  const [whole = '0', fraction] = fixed.split('.');

  // Group from the right, which is the only direction that works for any length.
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, style.group);
  const sign = value < 0 ? '-' : '';

  return sign + grouped + (fraction ? style.decimal + fraction : '');
};

/** `24.07.2026` in every language. */
export const formatDate = (value: Date | string | number): string => {
  const date = toDate(value);
  if (!date) return '';

  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
};

/** `13:10`. Seconds are noise in a schedule; ask for them explicitly if needed. */
export const formatTime = (value: Date | string | number, withSeconds = false): string => {
  const date = toDate(value);
  if (!date) return '';

  const base = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  return withSeconds ? `${base}:${pad(date.getSeconds())}` : base;
};

/** `24.07.2026 13:10`. */
export const formatDateTime = (value: Date | string | number): string => {
  const date = toDate(value);
  if (!date) return '';

  return `${formatDate(date)} ${formatTime(date)}`;
};

/**
 * `24 шілде 2026`, from the dictionary's own month names.
 *
 * Word order is the same in all three languages here — day, month, year — so one
 * pattern serves. A locale that needed another order would take its own branch.
 */
export const formatDateLong = (
  value: Date | string | number,
  monthNames: readonly string[],
): string => {
  const date = toDate(value);
  if (!date) return '';

  // `noUncheckedIndexedAccess` makes this explicit rather than hoping the array
  // has twelve entries.
  const month = monthNames[date.getMonth()] ?? '';

  return `${date.getDate()} ${month} ${date.getFullYear()}`;
};
